import { useEffect, ReactNode } from 'react';
import { css } from '@emotion/react';
import { useInView } from 'react-intersection-observer';
import { Box, Text } from '@opendoor/bricks/core';
import { FontSize } from '@opendoor/bricks/system/theme';
import useQueryParams from '../../helpers/queryParams';
import imgixUrl from '../../helpers/imgixUrl';

import { DESKTOP_HERO_URL, MOBILE_HERO_URL } from '../globals';
import AddressSearch, { AddressSearchProp } from '../shared/AddressSearch';
import { Token } from '@opendoor/bricks/system/token.types';

interface IProps {
  isVisible: (_: boolean) => void;
  returnExperienceBannerShown: boolean;
}

const Hero = ({ isVisible, returnExperienceBannerShown }: Partial<AddressSearchProp> & IProps) => {
  const [ref, inView, entry] = useInView();
  useEffect(() => {
    // for the first intersection observer event, the entry is undefined
    // as the element loads. We want to make sure not to flash the sticky
    // cta while it loads for the first time.
    if (!entry) {
      return;
    }
    isVisible(inView);
  }, [inView, isVisible, entry]);

  const { brandHeroVariant } = useQueryParams();

  const marginTop = returnExperienceBannerShown ? '0px' : '55px';
  const actionText = 'Get my free offer';
  let h1Text: ReactNode = 'Ready, set, sold.';

  if (brandHeroVariant == 'v1') {
    h1Text = 'The simpler way to sell';
  } else if (brandHeroVariant == 'v2') {
    h1Text = <Box maxW="600px">Sell your house directly to Opendoor</Box>;
  }

  const h2Text = 'Get an offer in minutes, sell in a matter of days.';

  //@ts-expect-error TODO - @growth to resolve
  const headerFontSizes: Token<FontSize> = ['29px', null, 's5', '52px'];

  return (
    <Box
      as="section"
      id="hero-container"
      className="hero-container"
      mt={marginTop}
      backgroundImage={[
        `url(${imgixUrl(MOBILE_HERO_URL)})`,
        null,
        `url(${imgixUrl(DESKTOP_HERO_URL)})`,
      ]}
      backgroundPosition="bottom center"
      backgroundRepeat="no-repeat"
      backgroundSize="cover"
    >
      <Box
        ref={ref}
        width={['90%', null, '70%', null]}
        m={['0px 5% 270px 5%', '70px 5% 300px 5%', '70px 15% 40px 15%', '185px 0 150px 10%']}
        display="inline-block"
        textAlign="left"
        css={css`
          @media (max-height: 568px) and (max-width: 766px) {
            margin: 10px 5% 130px 5% !important;
          }

          @media (max-height: 568px) and (min-width: 1200px) {
            margin: 30px 5% 20px 5% !important;
          }
        `}
      >
        <Text
          as="h1"
          lineHeight={1.2}
          color="neutrals0"
          mb={[5, null, 6]}
          mt={[7, null, 0]}
          textAlign={['center', null, 'left']}
          fontSize={headerFontSizes}
          fontWeight="semibold"
          position="relative"
        >
          {h1Text}
        </Text>
        <Box maxWidth="520px" m={['0 auto', null, '0 0']}>
          <AddressSearch
            inputLocation="hero-cta"
            trackingTaxonomy="new_homepage"
            actionText={actionText}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus={true}
          />
        </Box>
        <Text
          color="neutrals0"
          lineHeight={1.4}
          fontWeight="bold"
          fontSize={['s0', 's0', 's2']}
          textAlign={['center', null, 'left']}
          position="relative"
          className="sub-head"
          py={[3, 3, 3, 7]}
          mt={[null, null, 2]}
          mb={[3, 3, null]}
        >
          {h2Text}
        </Text>
      </Box>
    </Box>
  );
};

export default Hero;
